import React from 'react';
import { TrendingUpIcon, TrendingDownIcon, DollarSignIcon, ShoppingCartIcon, UsersIcon, ClockIcon, AlertCircleIcon, CheckCircleIcon, QrCodeIcon, BarChart3Icon, ArrowRightIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
const MerchantDashboard: React.FC = () => {
  // Mock data for the dashboard
  const dashboardData = {
    summary: {
      totalSales: 125750.65,
      transactionCount: 1250,
      averageTicket: 100.6,
      activeQRCodes: 15,
      pendingDisputes: 2,
      customerCount: 875
    },
    recentTransactions: [{
      id: 'TX12345',
      amount: 1250.75,
      status: 'completed',
      customer: 'John Smith',
      time: '2023-10-28T10:30:00Z',
      method: 'QR Code'
    }, {
      id: 'TX12346',
      amount: 750.25,
      status: 'completed',
      customer: 'Sarah Johnson',
      time: '2023-10-28T09:45:00Z',
      method: 'Card Payment'
    }, {
      id: 'TX12347',
      amount: 2450.0,
      status: 'pending',
      customer: 'Robert Williams',
      time: '2023-10-28T09:15:00Z',
      method: 'QR Code'
    }, {
      id: 'TX12348',
      amount: 450.5,
      status: 'failed',
      customer: 'Emily Brown',
      time: '2023-10-28T08:30:00Z',
      method: 'Card Payment'
    }, {
      id: 'TX12349',
      amount: 1875.25,
      status: 'completed',
      customer: 'Michael Davis',
      time: '2023-10-27T16:45:00Z',
      method: 'QR Code'
    }],
    salesTrend: {
      change: 12.5,
      direction: 'up'
    },
    transactionTrend: {
      change: 8.3,
      direction: 'up'
    },
    averageTicketTrend: {
      change: 3.7,
      direction: 'up'
    },
    customerTrend: {
      change: 5.2,
      direction: 'up'
    },
    qrCodeUsage: {
      totalScans: 875,
      uniqueScans: 650,
      conversionRate: 74.3
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  const formatNumber = (number: number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-4290">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-4291" />
            Completed
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" data-id="element-4292">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-4293" />
            Pending
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" data-id="element-4294">
            <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-4295" />
            Failed
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" data-id="element-4296">
            {status}
          </span>;
    }
  };
  return <div className="space-y-6" data-id="element-4297">
      <div data-id="element-4298">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4299">
          Dashboard
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-4300">
          Overview of your merchant activity and performance.
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4301">
        <Card className="bg-white dark:bg-gray-800" data-id="element-4302">
          <div className="flex items-center justify-between" data-id="element-4303">
            <div data-id="element-4304">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4305">
                Total Sales
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-4306">
                {formatCurrency(dashboardData.summary.totalSales)}
              </p>
              <div className="mt-1 flex items-center" data-id="element-4307">
                {dashboardData.salesTrend.direction === 'up' ? <TrendingUpIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-4308" /> : <TrendingDownIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-4309" />}
                <span className={`text-sm font-medium ${dashboardData.salesTrend.direction === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-4310">
                  {dashboardData.salesTrend.change}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-4311">
              <DollarSignIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-4312" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-4313">
          <div className="flex items-center justify-between" data-id="element-4314">
            <div data-id="element-4315">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4316">
                Total Transactions
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-4317">
                {formatNumber(dashboardData.summary.transactionCount)}
              </p>
              <div className="mt-1 flex items-center" data-id="element-4318">
                {dashboardData.transactionTrend.direction === 'up' ? <TrendingUpIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-4319" /> : <TrendingDownIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-4320" />}
                <span className={`text-sm font-medium ${dashboardData.transactionTrend.direction === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-4321">
                  {dashboardData.transactionTrend.change}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30" data-id="element-4322">
              <ShoppingCartIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" data-id="element-4323" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-4324">
          <div className="flex items-center justify-between" data-id="element-4325">
            <div data-id="element-4326">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4327">
                Average Ticket
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-4328">
                {formatCurrency(dashboardData.summary.averageTicket)}
              </p>
              <div className="mt-1 flex items-center" data-id="element-4329">
                {dashboardData.averageTicketTrend.direction === 'up' ? <TrendingUpIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-4330" /> : <TrendingDownIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-4331" />}
                <span className={`text-sm font-medium ${dashboardData.averageTicketTrend.direction === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-4332">
                  {dashboardData.averageTicketTrend.change}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30" data-id="element-4333">
              <BarChart3Icon className="h-6 w-6 text-green-600 dark:text-green-400" data-id="element-4334" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-4335">
          <div className="flex items-center justify-between" data-id="element-4336">
            <div data-id="element-4337">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4338">
                Active QR Codes
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-4339">
                {dashboardData.summary.activeQRCodes}
              </p>
              <div className="mt-1 flex items-center" data-id="element-4340">
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4341">
                  {dashboardData.qrCodeUsage.totalScans} total scans
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-indigo-100 dark:bg-indigo-900/30" data-id="element-4342">
              <QrCodeIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" data-id="element-4343" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-4344">
          <div className="flex items-center justify-between" data-id="element-4345">
            <div data-id="element-4346">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4347">
                Customers
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-4348">
                {formatNumber(dashboardData.summary.customerCount)}
              </p>
              <div className="mt-1 flex items-center" data-id="element-4349">
                {dashboardData.customerTrend.direction === 'up' ? <TrendingUpIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-4350" /> : <TrendingDownIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-4351" />}
                <span className={`text-sm font-medium ${dashboardData.customerTrend.direction === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-4352">
                  {dashboardData.customerTrend.change}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30" data-id="element-4353">
              <UsersIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" data-id="element-4354" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-4355">
          <div className="flex items-center justify-between" data-id="element-4356">
            <div data-id="element-4357">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4358">
                Pending Disputes
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-4359">
                {dashboardData.summary.pendingDisputes}
              </p>
              <div className="mt-1 flex items-center" data-id="element-4360">
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4361">
                  Requires attention
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30" data-id="element-4362">
              <AlertCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" data-id="element-4363" />
            </div>
          </div>
        </Card>
      </div>
      {/* Recent Transactions */}
      <Card className="bg-white dark:bg-gray-800" data-id="element-4364">
        <div className="flex items-center justify-between mb-4" data-id="element-4365">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-4366">
            Recent Transactions
          </h3>
          <a href="/merchant-portal/transactions" className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 flex items-center" data-id="element-4367">
            View all
            <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-4368" />
          </a>
        </div>
        <div className="overflow-x-auto" data-id="element-4369">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4370">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-4371">
              <tr data-id="element-4372">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4373">
                  Transaction ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4374">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4375">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4376">
                  Method
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4377">
                  Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4378">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4379">
              {dashboardData.recentTransactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-4380">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-4381">
                    {transaction.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4382">
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4383">
                    {transaction.customer}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4384">
                    {transaction.method}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4385">
                    {formatDate(transaction.time)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4386">
                    {getStatusBadge(transaction.status)}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Quick Actions */}
      <Card className="bg-white dark:bg-gray-800" data-id="element-4387">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-4388">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4389">
          <a href="/merchant-portal/qr-generator" className="flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors" data-id="element-4390">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800 mr-3" data-id="element-4391">
              <QrCodeIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-4392" />
            </div>
            <div data-id="element-4393">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4394">
                Generate QR Code
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4395">
                Create new payment QR codes
              </p>
            </div>
          </a>
          <a href="/merchant-portal/reports" className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors" data-id="element-4396">
            <div className="p-2 rounded-full bg-green-100 dark:bg-green-800 mr-3" data-id="element-4397">
              <BarChart3Icon className="h-5 w-5 text-green-600 dark:text-green-400" data-id="element-4398" />
            </div>
            <div data-id="element-4399">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4400">
                Run Reports
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4401">
                Generate custom transaction reports
              </p>
            </div>
          </a>
          <a href="/merchant-portal/campaigns" className="flex items-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors" data-id="element-4402">
            <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-800 mr-3" data-id="element-4403">
              <UsersIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-4404" />
            </div>
            <div data-id="element-4405">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4406">
                View Campaigns
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4407">
                Check your active promotions
              </p>
            </div>
          </a>
        </div>
      </Card>
    </div>;
};
export default MerchantDashboard;